import type { BusinessCategory, Vertical } from '../constants.js';
import type { DayHours } from './hours.js';
import type { OnboardingStep, TeamSize } from './types.js';
import type { Chapter } from './chapters.js';

/**
 * The business-onboarding wizard's draft — the SHAPE and its rules, with no
 * storage in it.
 *
 * ## Why this is in `packages/shared` and not in the app that writes it
 *
 * It began life inside `apps/mobile/src/onboarding/draft-store.ts`, where a
 * device-local AsyncStorage draft was the only thing that existed
 * (`docs/decisions/0027-onboarding-wizard-defers-all-persistence-to-final-submit.md`).
 * The web wizard makes that draft cross a device boundary: an owner types their
 * service menu on a laptop, scans a QR, and finishes on the phone. That only
 * works if BOTH clients and the server agree on one shape.
 *
 * ⚠️ **The failure mode this file exists to prevent is silent.** `normalizeDraft`
 * discards a draft whose `version` it does not recognise — deliberately, and
 * that rule predates this move. The moment two codebases can write the same
 * record, a shape that drifts on one side does not throw on the other: it makes
 * the phone quietly bin everything the owner typed on their computer. So this
 * module is the single definition, and `DRAFT_VERSION` is the single switch.
 *
 * ## When to bump `DRAFT_VERSION`, and when not to
 *
 * **Do not bump for an added field.** Default it inside `normalizeDraft`
 * instead — the precedent set by `hasMultipleTrades`, `categories`, `visited`
 * and `cancellationWindowMinutes`, each of which arrived without a bump. A bump
 * bins every in-flight draft on upgrade AND again on rollback, which is a real
 * cost paid by real owners mid-setup; an added field's absence costs a default.
 *
 * **Do bump when an existing field changes meaning or a step id disappears** —
 * i.e. when resuming would land the owner in a half-mapped state. That is the
 * v1 → v2 call recorded below, and it is the right trade: a few minutes of
 * retyping beats a wizard that resumes into a step that no longer exists.
 */

/** One service the owner has added locally (no `Service` row exists yet). */
export type DraftService = {
  id: string;
  name: string;
  durationMinutes: number;
  price: number | null;
  /** Which of the business's chosen trades this service is filed under. Null
   *  until the owner has picked any category at all, in which case the wizard
   *  falls back to the business's own vertical on submit - `service.create`
   *  requires a vertical, so this is never sent as null. */
  vertical: Vertical | null;
  /** LOCAL id into `WizardDraft.categories` - the owner-defined category this
   *  service was filed under, a separate axis from `vertical` above.
   *  `service.create` requires a `categoryId` server-side, so the wizard
   *  refuses to save a NEW service without a selection; `null` only survives
   *  here via a draft resumed from before this field existed. Added without a
   *  version bump - `normalizeDraft` defaults an absent value to `null`. */
  categoryId: string | null;
};

/** One locally-drafted, owner-named service category - like services/staff,
 *  no `Category` row exists until final submit, when the submit sequence
 *  creates one per entry here and remaps every service's `categoryId` to the
 *  real id it became (`submit.createdCategoryIds`). */
export type DraftCategory = { id: string; name: string };

/** One staff member the owner has added locally (no `Staff` row exists yet). */
export type DraftStaff = {
  id: string;
  name: string;
  title: string;
  photoUrl: string | null;
  /** LOCAL service ids (`DraftService.id`) this person performs. Mapped to
   *  server ids during submit via `submit.createdServiceIds`. Empty means
   *  "not specified" - the wizard does not link them to anything, matching
   *  what `staff.create` does with no service list. */
  serviceIds: string[];
};

/**
 * The current draft shape version.
 *
 * v1 -> v2: the `location` step merged into `basics`, `photo` and the new
 * `contacts` step moved to the end of the flow, services gained a `vertical`,
 * staff gained `serviceIds`, and the business gained `secondaryCategories`. A
 * v1 draft is discarded rather than migrated: the step it was parked on may no
 * longer exist, every added service would need a category the owner never
 * picked, and the draft represents at most a few minutes of typing the owner is
 * about to redo anyway in a re-ordered flow. Silently resuming into a
 * half-mapped state is the worse failure.
 */
export const DRAFT_VERSION = 2;

/**
 * The wizard's ENTIRE draft — everything a step's local state holds.
 *
 * Nothing in here is written to the business tables until the wizard's final
 * submit. Two things do leave the device early, and both are deliberate:
 *
 * 1. **Photos.** A picked photo uploads IMMEDIATELY via
 *    `business.createDraftUploadUrl` (scoped to the signed-in owner's userId
 *    rather than a businessId that doesn't exist yet) and only the resulting
 *    `https://` URL is ever stored here. A local `file://` URI from the image
 *    picker lives in a cache directory that is NOT guaranteed to survive an app
 *    restart, so storing one in a draft meant to survive exactly that would be
 *    silently unreliable — and it could not cross to another device at all.
 * 2. **The draft itself**, to `onboarding.saveDraft`, so the setup can move
 *    between a computer and a phone. This is a second exception to decision
 *    `0027`, and a compatible one: `0027`'s concern was half-filled `Business`
 *    rows indistinguishable from real ones, which a separate draft record does
 *    not create.
 *
 * Device contacts are deliberately NOT in here: the wizard records only whether
 * the owner opted IN (`importContacts`), and the address book itself is read on
 * the dedicated import screen after submit. Persisting names and phone numbers
 * of people who are not yet anyone's clients — now to a server, not just to
 * unencrypted local storage — would be a privacy regression for no benefit.
 *
 * `staffAdded[].id` / `services[].id` are CLIENT-generated ids, not server ids
 * — no `Service`/`Staff` row exists until final submit. They only need to be
 * stable enough to key `staffHoursById`, `staffAdded[].serviceIds` and the edit
 * affordances.
 *
 * `submit` tracks progress THROUGH the final multi-call submit sequence: a
 * network failure partway must not re-run an already-succeeded step on retry
 * (that would slug-conflict on a second `createOwn`, or double-create a
 * service/staff row). Maps each local id to the server id it became;
 * `businessId`/`profileSaved`/`published` are once-only flags. Left at its
 * all-`null`/empty default until the owner actually presses Submit.
 */
export type WizardDraft = {
  /** See `DRAFT_VERSION` — bumped only if this shape changes incompatibly.
   *  `normalizeDraft` discards (rather than crashes on) an unrecognised one. */
  version: typeof DRAFT_VERSION;
  step: OnboardingStep;
  /**
   * Chapters the owner has actually OPENED, which is what turns a card's mark
   * green or amber rather than leaving it blank.
   *
   * Additive and optional (defaulted in `normalizeDraft`), NOT a version bump.
   *
   * A caller unions this with `chaptersWithAnyAnswer(facts)`, so a draft
   * written before this field existed still shows its marks: anything the owner
   * actually answered is evidence enough. What only this can record is a
   * chapter opened and closed with nothing entered - which has earned its amber
   * mark, and which no answer can prove.
   */
  visited: Chapter[];
  bizName: string;
  bizSlug: string;
  /** The MAIN trade. */
  category: BusinessCategory | null;
  /** Additional trades, never containing `category` itself. Retained even
   *  while `hasMultipleTrades` is false, so an accidental un-tick doesn't
   *  lose the picks - see that field. */
  secondaryCategories: BusinessCategory[];
  /** Did the owner say the business offers more than one trade?
   *
   *  The source of truth for what actually gets SAVED: while this is false the
   *  business is single-trade no matter what `secondaryCategories` still holds.
   *  Kept separate from `secondaryCategories.length > 0` precisely so un-ticking
   *  can hide the extras without discarding them. */
  hasMultipleTrades: boolean;
  photoUrl: string | null;
  logoUrl: string | null;
  address: string;
  /**
   * The Places pick behind `address`, or `null` while the owner has only typed.
   *
   * `city` lives INSIDE this object rather than beside it so that it cannot
   * outlive the pin it came from: clearing the pin clears the city in the same
   * assignment. It is `null` when Places derived no locality for the address.
   *
   * The `city` key is additive and optional (defaulted in `normalizeDraft`),
   * NOT a version bump — a draft saved before #458 simply has no city, which is
   * exactly what it had at the time.
   */
  coords: { lat: number; lng: number; city: string | null } | null;
  phone: string;
  /** Minutes of notice to cancel for free; `null` until the owner picks one.
   *  0 is a real value here ("cancel any time"), so never treat this as
   *  falsy-vs-set. */
  cancellationWindowMinutes: number | null;
  services: DraftService[];
  categories: DraftCategory[];
  teamSize: TeamSize | null;
  staffAdded: DraftStaff[];
  staffHoursById: Record<string, DayHours[]>;
  description: string;
  /** Whether the owner asked to bring their address book across. Just the
   *  intent - see the note on contacts above. */
  importContacts: boolean;
  /**
   * What the submit has already achieved, so a retry does not redo it.
   *
   * ⚠️ **Six of these eight are VESTIGIAL since 2026-08-24**, when the mobile
   * wizard's submit became one atomic `business.completeSetup` call instead of
   * nine separate writes. Nothing writes or reads `profileSaved`,
   * `createdServiceIds`, `createdCategoryIds`, `hoursSetForStaffId`,
   * `servicesSetForStaffId` or `published` any more — a partial submit cannot
   * happen, so there is no partial progress to record.
   *
   * They are still here rather than deleted because this shape is PERSISTED,
   * on the device and in the server-side `onboarding_draft` row, and removing
   * fields from a stored payload is its own migration. Do not read them and do
   * not add new callers; treat a non-empty value as leftover from a draft
   * written by an older build.
   *
   * The two that ARE live:
   *  - `businessId` — set once `completeSetup` succeeds, so a retry after a
   *    later failure does not create a second business.
   *  - `createdStaffIds` — local staff id -> the row it became, which is what
   *    the app-access invites resolve through on that retry.
   */
  submit: {
    businessId: string | null;
    /** local staff id -> the server id it became. */
    createdStaffIds: Record<string, string>;
    /** @deprecated vestigial — see the note above. */
    profileSaved: boolean;
    /** @deprecated vestigial — see the note above. */
    createdServiceIds: Record<string, string>;
    /** @deprecated vestigial — see the note above. */
    createdCategoryIds: Record<string, string>;
    /** @deprecated vestigial — see the note above. */
    hoursSetForStaffId: Record<string, boolean>;
    /** @deprecated vestigial — see the note above. */
    servicesSetForStaffId: Record<string, boolean>;
    /** @deprecated vestigial — see the note above. */
    published: boolean;
  };
};

export function emptyDraft(): WizardDraft {
  return {
    version: DRAFT_VERSION,
    step: 'welcome',
    visited: [],
    bizName: '',
    bizSlug: '',
    category: null,
    secondaryCategories: [],
    hasMultipleTrades: false,
    photoUrl: null,
    logoUrl: null,
    address: '',
    coords: null,
    phone: '',
    cancellationWindowMinutes: null,
    services: [],
    categories: [],
    teamSize: null,
    staffAdded: [],
    staffHoursById: {},
    description: '',
    importContacts: false,
    submit: {
      businessId: null,
      profileSaved: false,
      createdServiceIds: {},
      createdStaffIds: {},
      createdCategoryIds: {},
      hoursSetForStaffId: {},
      servicesSetForStaffId: {},
      published: false,
    },
  };
}

/**
 * Bring a parsed draft up to the current shape, or reject it.
 *
 * `null` means "start fresh" and is returned for anything that cannot be
 * trusted: a non-object, or a `version` this build does not recognise (corrupt
 * storage, a draft written by an older build, or a NEWER build's shape read by
 * an older one — the cross-device case, where a phone on an old release may now
 * read what a freshly-deployed web wizard wrote).
 *
 * Every defaulting rule below exists because its field was added WITHOUT a
 * version bump. Each default is chosen to reproduce the behaviour a draft
 * written before that field existed already had, so normalising is never lossy.
 */
export function normalizeDraft(parsed: unknown): WizardDraft | null {
  if (typeof parsed !== 'object' || parsed === null) return null;
  const candidate = parsed as Partial<WizardDraft>;
  if (candidate.version !== DRAFT_VERSION) return null;

  const draft = candidate as WizardDraft;

  // Written before `hasMultipleTrades` existed: derive it from the extras that
  // are already there, which reproduces the old behaviour exactly (the
  // secondary block used to show whenever a main trade was picked).
  if (typeof (draft as Partial<WizardDraft>).hasMultipleTrades !== 'boolean') {
    draft.hasMultipleTrades = (draft.secondaryCategories ?? []).length > 0;
  }
  if (!Array.isArray((draft as Partial<WizardDraft>).secondaryCategories)) {
    draft.secondaryCategories = [];
  }
  if (!Array.isArray((draft as Partial<WizardDraft>).categories)) {
    draft.categories = [];
  }
  // Written before chapters existed. An empty default is correct rather than
  // lossy: a caller unions this with what the answers themselves prove.
  if (!Array.isArray((draft as Partial<WizardDraft>).visited)) {
    draft.visited = [];
  }
  // Written before the wizard asked for a cancellation policy. `null` is the
  // honest default - it means "not answered", which is exactly true of a draft
  // saved before the question existed. Explicit rather than relying on the key
  // being absent, since 0 is a valid answer and must survive a round trip.
  if (typeof (draft as Partial<WizardDraft>).cancellationWindowMinutes !== 'number') {
    draft.cancellationWindowMinutes = null;
  }
  // Written before the resolve returned a `city` (#458). `null` reproduces the
  // old behaviour exactly: those drafts never carried one, and the business
  // they create simply has no city until the owner re-picks their address.
  if (draft.coords && typeof draft.coords.city !== 'string') {
    draft.coords = { lat: draft.coords.lat, lng: draft.coords.lng, city: null };
  }
  if (!Array.isArray((draft as Partial<WizardDraft>).services)) {
    draft.services = [];
  }
  if (!Array.isArray((draft as Partial<WizardDraft>).staffAdded)) {
    draft.staffAdded = [];
  }
  if (typeof (draft as Partial<WizardDraft>).staffHoursById !== 'object' || !draft.staffHoursById) {
    draft.staffHoursById = {};
  }
  // `submit` is absent on any draft that never reached the final step, which is
  // most of them — and every field below is read during a retry, so a missing
  // one would re-run a step that already succeeded.
  const submit = (draft as Partial<WizardDraft>).submit;
  draft.submit = {
    businessId: submit?.businessId ?? null,
    profileSaved: submit?.profileSaved ?? false,
    createdServiceIds: submit?.createdServiceIds ?? {},
    createdStaffIds: submit?.createdStaffIds ?? {},
    createdCategoryIds: submit?.createdCategoryIds ?? {},
    hoursSetForStaffId: submit?.hoursSetForStaffId ?? {},
    servicesSetForStaffId: submit?.servicesSetForStaffId ?? {},
    published: submit?.published ?? false,
  };

  draft.services = draft.services.map((svc) => ({
    ...svc,
    categoryId: (svc as Partial<DraftService>).categoryId ?? null,
  }));

  return draft;
}
